// D6 — RealityAI Guide: D5 backend API client (TechSpec §24-28).
//
// The D5 backend is the SINGLE authority. This client only forwards calls and
// normalizes errors — it does NOT re-implement FAQ/troubleshooting/search/ACL
// (Master §17-18). Errors surface as `GuideApiError` with a stable `kind` so
// the UI can map them to ERROR / no-result / disabled states deterministically.

import { request, ApiError, ApiError as _ApiError } from "@/lib/api";
import type {
  GuideQueryResponse,
  GuideStatus,
  GuideContext,
  GuideMode,
} from "./types";

/** Stable error classification for the assistant/panel state machine. */
export type GuideErrorKind =
  | "network" // fetch threw (no response)
  | "server" // 5xx
  | "disabled" // 404 → Guide not visible (flag OFF or preference OFF)
  | "validation" // 400 (e.g. empty/oversized question)
  | "rate_limited" // 429
  | "auth" // 401
  | "unknown";

export interface GuideApiError extends Error {
  kind: GuideErrorKind;
  status?: number;
}

function classify(status: number | undefined): GuideErrorKind {
  if (status === undefined) return "network";
  if (status === 404) return "disabled";
  if (status === 429) return "rate_limited";
  if (status === 401) return "auth";
  if (status >= 500) return "server";
  if (status >= 400) return "validation";
  return "unknown";
}

function wrap(e: unknown): GuideApiError {
  if (e instanceof ApiError) {
    return {
      name: "GuideApiError",
      kind: classify(e.status),
      status: e.status,
      message: e.message,
    };
  }
  return {
    name: "GuideApiError",
    kind: "network",
    message: e instanceof Error ? e.message : "Неизвестная ошибка",
  };
}

export interface GuideQueryOptions {
  mode?: GuideMode | null;
  context?: GuideContext;
  signal?: AbortSignal;
}

/**
 * Run the deterministic Guide pipeline (D5). Returns the D5 response; the
 * caller renders it verbatim (no frontend answer generation).
 */
export async function guideQuery(
  question: string,
  options: GuideQueryOptions = {},
): Promise<GuideQueryResponse> {
  try {
    return await request<GuideQueryResponse>("/api/guide/query", {
      method: "POST",
      body: JSON.stringify({
        question,
        mode: options.mode ?? null,
        context: options.context ?? undefined,
      }),
      signal: options.signal,
    });
  } catch (e) {
    throw wrap(e);
  }
}

/** Effective Guide visibility (D5). 404 is NOT expected here (status always
 *  answers), but a disabled Guide still returns visible=false. */
export async function guideStatus(signal?: AbortSignal): Promise<GuideStatus> {
  try {
    return await request<GuideStatus>("/api/guide/status", { signal });
  } catch (e) {
    // A 404/401 on status means "not visible to you" — treat as not visible
    // rather than an error, so the mascot simply does not appear.
    if (e instanceof _ApiError && (e.status === 404 || e.status === 401)) {
      return { visible: false, system_enabled: false, user_preference: false };
    }
    throw wrap(e);
  }
}

export interface GuideFeedbackResult {
  status: string;
  query_id: string;
  helpful: boolean;
}

/** Helpful / Not Helpful (D5). `helpful` is a strict JSON bool. */
export async function guideFeedback(
  queryId: string,
  helpful: boolean,
  signal?: AbortSignal,
): Promise<GuideFeedbackResult> {
  try {
    return await request<GuideFeedbackResult>("/api/guide/feedback", {
      method: "POST",
      body: JSON.stringify({ query_id: queryId, helpful }),
      signal,
    });
  } catch (e) {
    throw wrap(e);
  }
}

// --- user preference (D5) -------------------------------------------------
// D5 stores the Guide ON/OFF under the key `realityai_guide_enabled`
// (backend/app/services/docs/preferences.py ALLOWED_KEYS). The frontend reads
// and writes only that key through the canonical /api/users/preferences API.

export const GUIDE_PREF_KEY = "realityai_guide_enabled";

export interface UserPreferences {
  [key: string]: unknown;
}

export async function fetchPreferences(
  signal?: AbortSignal,
): Promise<UserPreferences> {
  return request<UserPreferences>("/api/users/preferences", { signal });
}

export async function updatePreferences(
  prefs: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<UserPreferences> {
  return request<UserPreferences>("/api/users/preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
    signal,
  });
}

export async function fetchGuidePreference(
  signal?: AbortSignal,
): Promise<boolean> {
  const all = await fetchPreferences(signal);
  const v = all[GUIDE_PREF_KEY];
  // D5 default is `true`; a missing row means the default applies.
  return typeof v === "boolean" ? v : true;
}

export async function setGuidePreference(
  enabled: boolean,
  signal?: AbortSignal,
): Promise<boolean> {
  await updatePreferences({ [GUIDE_PREF_KEY]: enabled }, signal);
  return enabled;
}

export { ApiError };
